import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { get, upperFirst } from 'lodash/fp';
import { createSelector } from 'reselect';

import { CompanyOptions } from '@mews-commander/data-access';
import { ApplicationState, WithScope } from '@mews-commander/types';
import { createAsyncMetaSelectors, createScopedSelector, Selector } from '@mews/framework';
import { getDurationAsDays } from '@mews-commander/utils';
import { getCompanyLegalEntityIdentifiersCodeValue } from '@mews-commander/legacy-entities/src/ducks/legalEntityIdentifiers';
import {
    areAddedFeesToInvoices,
    getCompany,
    getCompanyClassifications,
    isCompanyInvoiceable,
    areInvoicesTaxDeductable,
    areInvoicesTaxDeducted,
} from '@mews-commander/legacy-entities/src/ducks';
import { createEntityFormattedMetaSelector } from '@mews-commander/legacy-entities/src/selectors';
import { moduleSelector } from './module';
import { initialState } from './reducers/state';

const moduleStateParentSelector = createSelector(moduleSelector, get('state'));
const scopedModuleSelector: Selector<ApplicationState, WithScope, typeof initialState> = createScopedSelector({
    parentSelector: moduleStateParentSelector,
    initialState,
});
const getModuleMeta = createSelector(scopedModuleSelector, (state) => state.meta);

export const [isInitFetching, hasInitFailed] = createAsyncMetaSelectors(getModuleMeta);

export const getContactFormInitialValues = createSelector(
    getCompany,
    getCompanyLegalEntityIdentifiersCodeValue,
    (company, legalEntityIdentifiers) => ({
        name: company?.name || null,
        externalIdentifier: company?.externalIdentifier || null,
        motherCompanyId: company?.motherCompanyId || null,
        department: company?.department || null,
        email: company?.email || null,
        websiteUrl: company?.website || null,
        referenceId: company?.referenceId || null,
        telephone: company?.telephone || null,
        notes: company?.notes || null,
        fiscalIdentifier: company?.fiscalIdentifier || null,
        accountingCode: company?.accountingCode || null,
        taxIdentifier: company?.taxIdentifier || null,
        additionalTaxIdentifier: company?.additionalTaxIdentifier || null,
        billingCode: company?.billingCode || null,
        dunsNumber: company?.dunsNumber || null,
        legalEntityIdentifiers,
    })
);

const getInitialCompanyClassifications = createSelector(getCompanyClassifications, (classifications) => {
    if (!classifications || record.isEmpty(classifications as Record<string, boolean>)) {
        return [];
    }

    return pipe(
        classifications as Record<string, boolean>,
        record.filter((value) => value),
        record.keys,
        array.map((key) => upperFirst(key))
    );
});
export const initializeCompanyPaymentOptions = createSelector(
    isCompanyInvoiceable,
    areInvoicesTaxDeductable,
    areAddedFeesToInvoices,
    areInvoicesTaxDeducted,
    (isInvoiceable, allowTaxDeductedPayments, areFeesToInvoices, invoicesTaxDeducted) => {
        const options = [];
        const canAddTaxDeductedPaymentToInvoices = allowTaxDeductedPayments && invoicesTaxDeducted;
        if (areFeesToInvoices) options.push(CompanyOptions.AddFeesToInvoices);
        if (isInvoiceable) options.push(CompanyOptions.Invoiceable);
        if (canAddTaxDeductedPaymentToInvoices) {
            options.push(CompanyOptions.AddTaxDeductedPaymentToInvoices);
        }
        return options;
    }
);
export const getAdditionalInfoFormInitialValues = createSelector(
    getCompany,
    getInitialCompanyClassifications,
    initializeCompanyPaymentOptions,
    (company, classifications, options) => ({
        classifications,
        options,
        contact: company?.contact || null,
        contactPerson: company?.contactPerson || null,
        iata: company?.iata || null,
        invoiceDueInterval: company?.invoiceDueInterval ? getDurationAsDays(company?.invoiceDueInterval) : null,
        sourceId: company?.sourceId || null,
    })
);

export const getCompanyFormattedMeta = createEntityFormattedMetaSelector(getCompany);

export const getNotesFormInitialValues = () => ({
    notes: null,
});

export const getCustomerFormInitialValues = () => ({
    customerId: null,
});

export const getContractFormInitialValues = () => ({
    negotiatedRates: '',
    contractStartDate: '',
    contractEndDate: '',
});

export const isCompanyDetailInitialized = createSelector(scopedModuleSelector, get('initialized'));
